// src/components/ServicesThreePillars.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const PHONE = "584228180393";
const buildWhatsAppURL = (message) =>
  `https://wa.me/${PHONE}?text=${encodeURIComponent(message)}`;

const ServicesThreePillars = () => {
  const { t } = useLanguage();

  const pillars = [
    {
      id: "auditoria-rescate",
      tag: t("services.pillars.audit.tag"),
      tagColor: "bg-cyan-500 text-slate-950 font-black",
      borderColor: "border-cyan-500/40 hover:border-cyan-400",
      glowColor: "hover:shadow-[0_0_35px_rgba(6,182,212,0.25)]",
      icon: "🛡️",
      title: t("services.pillars.audit.title"),
      subtitle: t("services.pillars.audit.subtitle"),
      description: t("services.pillars.audit.description"),
      features: t("services.pillars.audit.features") || [],
      ctaText: t("services.pillars.audit.cta"),
      ctaLink: buildWhatsAppURL(t("services.pillars.audit.msg")),
      extraAction: {
        to: "/planes",
        text: "¿Necesitas soporte mensual continuo? Ver Planes",
      },
    },
    {
      id: "qa-google-play",
      tag: t("services.pillars.qa.tag"),
      tagColor: "bg-amber-400 text-slate-950 font-black",
      borderColor: "border-amber-400/50 hover:border-amber-400",
      glowColor: "hover:shadow-[0_0_35px_rgba(251,191,36,0.25)]",
      icon: "🚀",
      title: t("services.pillars.qa.title"),
      subtitle: t("services.pillars.qa.subtitle"),
      description: t("services.pillars.qa.description"),
      features: t("services.pillars.qa.features") || [],
      ctaText: t("services.pillars.qa.cta"),
      ctaLink: buildWhatsAppURL(t("services.pillars.qa.msg")),
    },
    {
      id: "desarrollo-medida",
      tag: t("services.pillars.custom.tag"),
      tagColor: "bg-purple-500 text-white font-black",
      borderColor: "border-purple-500/40 hover:border-purple-400",
      glowColor: "hover:shadow-[0_0_35px_rgba(168,85,247,0.25)]",
      icon: "⚡",
      title: t("services.pillars.custom.title"),
      subtitle: t("services.pillars.custom.subtitle"),
      description: t("services.pillars.custom.description"),
      features: t("services.pillars.custom.features") || [],
      ctaText: t("services.pillars.custom.cta"),
      ctaLink: buildWhatsAppURL(t("services.pillars.custom.msg")),
      extraAction: {
        to: "/planes",
        text: "Conoce nuestros planes de acompañamiento mensual",
      },
    },
  ];

  return (
    <section id="servicios" className="w-full max-w-7xl mx-auto px-6 py-14 space-y-12">
      {/* HEADER DE LA SECCIÓN */}
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-700 dark:text-cyan-300 font-extrabold text-xs uppercase tracking-wider shadow-sm">
          <span>⚡</span>
          <span>{t("nav.services")}</span>
        </span>

        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight">
          {t("services.title")}
        </h2>

        <p className="text-slate-600 dark:text-slate-300 text-base md:text-lg leading-relaxed font-normal">
          {t("services.subtitle")}
        </p>

        {/* CALLOUT SUTIL PARA PROMOVER LOS PLANES MENSUALES */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-cyan-500/10 border border-cyan-500/25 text-xs text-slate-700 dark:text-slate-300">
          <span className="text-cyan-500 font-bold">💡 Tip:</span>
          <span>¿Buscas que seamos tu departamento técnico mes a mes?</span>
          <Link
            to="/planes"
            className="font-extrabold text-cyan-600 dark:text-cyan-400 hover:underline inline-flex items-center gap-1"
          >
            Ver Planes de Acompañamiento →
          </Link>
        </div>
      </div>

      {/* LOS 3 PILARES: GRID DE TARJETAS DE ALTO VALOR */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
        {pillars.map((pillar, index) => (
          <motion.div
            key={pillar.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            className={`
              relative
              rounded-[2.2rem]
              border-2
              ${pillar.borderColor}
              bg-white
              dark:bg-[#0c1824]
              p-8
              flex
              flex-col
              justify-between
              shadow-xl
              transition-all
              duration-300
              ${pillar.glowColor}
            `}
          >
            {/* PARTE SUPERIOR */}
            <div className="space-y-6">
              {/* TAG & ICON */}
              <div className="flex items-center justify-between gap-2">
                <span className={`px-3.5 py-1.5 rounded-full text-[10px] tracking-wider uppercase shadow-sm ${pillar.tagColor}`}>
                  {pillar.tag}
                </span>
                <span className="text-3xl">{pillar.icon}</span>
              </div>

              {/* TÍTULO Y SUBTÍTULO */}
              <div className="space-y-1.5">
                <h3 className="text-2xl font-black text-slate-900 dark:text-white leading-snug">
                  {pillar.title}
                </h3>
                <p className="text-xs font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wide">
                  {pillar.subtitle}
                </p>
              </div>

              {/* DESCRIPCIÓN */}
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-normal">
                {pillar.description}
              </p>

              {/* FEATURES BULLETS */}
              <div className="pt-4 border-t border-slate-200 dark:border-white/10 space-y-3">
                <p className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Qué incluye:
                </p>
                <ul className="space-y-2.5">
                  {pillar.features.map((feat, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-slate-200 font-medium leading-relaxed"
                    >
                      <span className="w-4 h-4 rounded-full bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 font-black flex items-center justify-center text-[10px] shrink-0 mt-0.5">
                        ✓
                      </span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* CTAS INFERIORES */}
            <div className="pt-8 mt-6 border-t border-slate-200 dark:border-white/10 space-y-3">
              <a
                href={pillar.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  w-full
                  py-3.5
                  px-6
                  rounded-2xl
                  bg-slate-900
                  hover:bg-cyan-500
                  text-white
                  hover:text-slate-950
                  dark:bg-white/10
                  dark:hover:bg-cyan-400
                  dark:hover:text-slate-950
                  font-extrabold
                  text-xs
                  tracking-wider
                  uppercase
                  transition-all
                  duration-300
                  flex
                  items-center
                  justify-center
                  gap-2
                  shadow-md
                "
              >
                <span>{pillar.ctaText}</span>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>

              {pillar.extraAction && (
                <Link
                  to={pillar.extraAction.to}
                  className="block text-center text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline pt-1"
                >
                  {pillar.extraAction.text} →
                </Link>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default ServicesThreePillars;
